import {
  CodeSandboxOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { EmptyState } from '../../../components/ui/EmptyState'
import { useToast } from '../../../components/ui/ToastProvider'
import type { ToastTone } from '../../../components/ui/ToastProvider'
import { formatDate, formatLongDate, formatPrice } from '../format'
import type { PartDetail, PartRevision, PartRevisionFile, PartRevisionOrder } from '../types'

/**
 * SECURITY (low, defense-in-depth) — every file `url` on this tab is
 * server-provided (`PartRevisionFile.url` / `PartRevisionOrder.files[].url`);
 * this guard rejects anything that isn't a plain `http(s)` link (e.g. a
 * `javascript:` URL or a malformed string) before it ever reaches
 * `window.open`, so a compromised/misconfigured BFF response can't drive an
 * open-redirect or script-URL `window.open` call from this component.
 */
function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Shared open-or-toast sequence (code-review UID-332 dedup — was
 * triplicated across the revision file chip and the order's drawing/3D
 * buttons). Preserves the exact 3-way branch each call site had: a safe
 * `http(s)` URL opens via `window.open` with `noopener,noreferrer`; a
 * present-but-unsafe URL shows the same invalid-link error toast every site
 * used; no URL at all shows the CALLER's own "not available" message, since
 * that message differs per site (file name vs. "drawing" vs. "3D model").
 */
function openFileOrToast(
  file: Pick<PartRevisionFile, 'url'> | undefined,
  showToast: (message: string, tone?: ToastTone) => void,
  notAvailableMessage: string,
): void {
  if (file?.url && isSafeHttpUrl(file.url)) {
    window.open(file.url, '_blank', 'noopener,noreferrer')
  } else if (file?.url) {
    showToast('This file’s link looks invalid and can’t be opened.', 'error')
  } else {
    showToast(notAvailableMessage, 'info')
  }
}

export interface PartRevisionsSectionProps {
  readonly part: PartDetail
  /** Fires when the user clicks a revision's "Add to quote" banner CTA
   * (`canAddToQuote`, `dc.html:1767-1772`). */
  readonly onAddToQuote: (revision: PartRevision) => void
  /** Reorders a previously-purchased order's part (mirrors the Library's
   * existing `onReorder` — same real POST, not a new endpoint). */
  readonly onReorderOrder: (order: PartRevisionOrder) => void
}

/**
 * Part detail "Purchased revisions" tab (AC-F3, UID-332/ADR-0113 B2,
 * `dc.html:1749-1810`). Reads the rich `revisionGroups[]` when present;
 * falls back to the flat `revisions: PartRevisionOrder[]` as plain rows
 * (the pre-UID-332 shape) when `revisionGroups` is absent or empty — so a
 * BFF that hasn't seeded the nested projection yet still renders correctly.
 */
export function PartRevisionsSection({
  part,
  onAddToQuote,
  onReorderOrder,
}: PartRevisionsSectionProps) {
  const groups = part.revisionGroups

  if (groups && groups.length > 0) {
    const latestPurchasedRevision = findLatestPurchasedRevision(groups)
    return (
      <div className="part-detail-revisions">
        {groups.map((revision) => (
          <RevisionCard
            key={revision.revision}
            revision={revision}
            isLatestPurchased={revision === latestPurchasedRevision}
            onAddToQuote={() => onAddToQuote(revision)}
            onReorderOrder={onReorderOrder}
          />
        ))}
      </div>
    )
  }

  if (part.revisions.length === 0) {
    return (
      <EmptyState title="No purchases yet for this revision" detail="Add it to a quote to order." />
    )
  }

  return <FlatRevisionsTable revisions={part.revisions} />
}

/**
 * dc.html:1767-1772 — the "Latest purchased revision" banner belongs on
 * whichever revision group was MOST RECENTLY purchased, i.e. the group
 * whose orders contain the newest `orderedOn` across ALL revisions — not
 * merely the one flagged `canAddToQuote` (that field can be absent/false
 * from a not-yet-seeded BFF while the revision was still the latest
 * purchased). A revision with zero orders is never "purchased" and can
 * never win this comparison.
 */
function findLatestPurchasedRevision(groups: readonly PartRevision[]): PartRevision | undefined {
  let latest: PartRevision | undefined
  let latestTime = Number.NEGATIVE_INFINITY
  for (const revision of groups) {
    for (const order of revision.orders) {
      const time = new Date(order.orderedOn).getTime()
      if (Number.isNaN(time)) continue
      if (time > latestTime) {
        latestTime = time
        latest = revision
      }
    }
  }
  return latest
}

/** The most recent `orderedOn` among a revision's own orders, or `null`
 * when it has none (never "purchased"). */
function latestOrderDate(orders: readonly PartRevisionOrder[]): string | null {
  let latest: string | null = null
  let latestTime = Number.NEGATIVE_INFINITY
  for (const order of orders) {
    const time = new Date(order.orderedOn).getTime()
    if (Number.isNaN(time)) continue
    if (time > latestTime) {
      latestTime = time
      latest = order.orderedOn
    }
  }
  return latest
}

function FlatRevisionsTable({
  revisions,
}: {
  readonly revisions: readonly PartRevisionOrder[]
}) {
  return (
    // UID-audit fix (Bug 3, 2026-09-10): this table previously rendered
    // with NO wrapping scroll container at all, unlike every other
    // `.parts-table` consumer — under crowding it spilled directly into the
    // page layout with no scroll cue. Mirrors the `.parts-table-card` /
    // `.staging-bom-tree-scroll` wrapper pattern used elsewhere in the app.
    <div className="part-detail-flat-revisions-table-scroll">
      <table className="parts-table">
        <thead>
          <tr>
            <th scope="col">Revision</th>
            <th scope="col">Order</th>
            <th scope="col">Ordered on</th>
            <th scope="col">Ordered by</th>
            <th scope="col">Qty</th>
            <th scope="col">Unit price</th>
          </tr>
        </thead>
        <tbody>
          {revisions.map((rev) => (
            <tr key={rev.orderId}>
              <td>{rev.revision}</td>
              <td>{rev.orderId}</td>
              <td>{formatDate(rev.orderedOn)}</td>
              <td>{rev.orderedBy}</td>
              <td>{rev.quantity}</td>
              <td>{rev.unitPrice ? formatPrice(rev.unitPrice) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RevisionCard({
  revision,
  isLatestPurchased,
  onAddToQuote,
  onReorderOrder,
}: {
  readonly revision: PartRevision
  readonly isLatestPurchased: boolean
  readonly onAddToQuote: () => void
  readonly onReorderOrder: (order: PartRevisionOrder) => void
}) {
  const files = revision.files ?? []
  const changedFields = revision.changedFields ?? []
  const lastPurchasedOn = latestOrderDate(revision.orders)

  return (
    <div className="card part-detail-revision-card">
      <div className="part-detail-revision-card-top">
        <div className="part-detail-revision-thumb">
          <div
            className="part-detail-revision-thumb-img"
            style={
              revision.thumbnailUrl ? { backgroundImage: `url(${revision.thumbnailUrl})` } : {}
            }
          />
          <div className="part-detail-revision-thumb-label">{revision.revision}</div>
        </div>

        <div className="part-detail-revision-body">
          <div className="part-detail-revision-title-row">
            <span className="part-detail-revision-name">{revision.name ?? revision.revision}</span>
            {revision.superseded && <Chip tone="neutral">Superseded</Chip>}
          </div>
          {revision.summary && <p className="caption-text">{revision.summary}</p>}

          {files.length > 0 && (
            <div className="part-detail-file-chips">
              {files.map((file) => (
                <RevisionFileChip key={`${file.kind}-${file.name}`} file={file} />
              ))}
            </div>
          )}

          {revision.note && (
            <p className="part-detail-revision-note">&ldquo;{revision.note}&rdquo;</p>
          )}

          {changedFields.length > 0 && (
            <div className="part-detail-changed-tags">
              {changedFields.map((field) => (
                <span key={field} className="part-detail-changed-tag">
                  {field} changed
                </span>
              ))}
            </div>
          )}
        </div>

        {isLatestPurchased && lastPurchasedOn && (
          <div className="part-detail-add-to-quote-banner">
            <div>
              <p className="part-detail-add-to-quote-title">Latest purchased revision</p>
              <p className="caption-text">
                This revision was last purchased on {formatLongDate(lastPurchasedOn)}
              </p>
            </div>
            {revision.canAddToQuote !== false && (
              <Button variant="primary" size="sm" onClick={onAddToQuote}>
                Add to quote
              </Button>
            )}
          </div>
        )}
      </div>

      {revision.orders.length > 0 ? (
        <RevisionOrdersTable orders={revision.orders} onReorderOrder={onReorderOrder} />
      ) : (
        <div className="part-detail-revision-no-orders">
          No purchases yet for this revision — add it to a quote to order.
        </div>
      )}
    </div>
  )
}

function RevisionFileChip({ file }: { readonly file: PartRevisionFile }) {
  const { showToast } = useToast()
  return (
    <button
      type="button"
      className="part-detail-file-chip"
      onClick={() => openFileOrToast(file, showToast, `${file.name} isn’t available yet.`)}
    >
      {file.kind === 'drawing' ? (
        <FileTextOutlined aria-hidden="true" />
      ) : (
        // dc.html:1798 — an isometric cube/box glyph for the 3D-model file,
        // NOT a grid (the old `AppstoreOutlined`).
        <CodeSandboxOutlined aria-hidden="true" />
      )}
      {file.name}
    </button>
  )
}

function RevisionOrdersTable({
  orders,
  onReorderOrder,
}: {
  readonly orders: readonly PartRevisionOrder[]
  readonly onReorderOrder: (order: PartRevisionOrder) => void
}) {
  const { showToast } = useToast()
  return (
    <table className="parts-table part-detail-revision-orders-table">
      <thead>
        <tr>
          <th scope="col">Order name</th>
          <th scope="col">Purchased on</th>
          <th scope="col">Requirements</th>
          <th scope="col">Price</th>
          <th scope="col">Qty</th>
          <th scope="col">Lead time</th>
          <th scope="col">Purchased by</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((order) => {
          const files = order.files ?? []
          const drawing = files.find((file) => file.kind === 'drawing')
          const model = files.find((file) => file.kind === 'model_3d')
          return (
            <tr key={order.orderId}>
              <td>
                <div>{order.name ?? order.orderId}</div>
                {order.status && <Chip tone="info">{order.status}</Chip>}
              </td>
              <td>{formatDate(order.orderedOn)}</td>
              <td>{order.requirements ?? '—'}</td>
              <td>{order.unitPrice ? formatPrice(order.unitPrice) : '—'}</td>
              <td>{order.quantity}</td>
              <td>{typeof order.leadTimeDays === 'number' ? `${order.leadTimeDays}d` : '—'}</td>
              <td>{order.orderedBy}</td>
              <td className="part-detail-order-actions">
                <button
                  type="button"
                  title="Drawing"
                  className="icon-btn"
                  onClick={() => openFileOrToast(drawing, showToast, 'No drawing available yet.')}
                >
                  <FileTextOutlined aria-hidden="true" />
                </button>
                <button
                  type="button"
                  title="3D model"
                  className="icon-btn"
                  onClick={() => openFileOrToast(model, showToast, 'No 3D model available yet.')}
                >
                  {/* dc.html:1798 — isometric cube/box glyph, NOT a grid
                      (the old `AppstoreOutlined`). */}
                  <CodeSandboxOutlined aria-hidden="true" />
                </button>
                <button
                  type="button"
                  title="Reorder"
                  // dc.html:1799 `fx-cartbtn` — the reorder action is a
                  // TINTED button, visually distinct from the plain
                  // white/neutral-border drawing & 3D-model buttons.
                  className="icon-btn part-detail-order-action-cart"
                  onClick={() => onReorderOrder(order)}
                >
                  <ShoppingCartOutlined aria-hidden="true" />
                </button>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
